import { App } from '@capacitor/app';
import { useCallback, useEffect, useRef, useState } from 'react';

import {
  getPushPermissionStatus,
  type PushPermissionStatus,
  requestPushPermissionAndRegister,
} from '@/core/lib/push-notifications';
import { isCapacitorNative } from '@/core/utils/capacitor.utils';

type RefreshOptions = {
  /** Re-check after returning from system settings; may register without showing a dialog. */
  attemptRegister?: boolean;
};

export function usePushPermissionStatus(active = true) {
  const [status, setStatus] = useState<PushPermissionStatus | null>(null);
  const activeRef = useRef(active);

  useEffect(() => {
    activeRef.current = active;
  }, [active]);

  const refresh = useCallback(
    async (options?: RefreshOptions): Promise<PushPermissionStatus | null> => {
      if (!activeRef.current || !isCapacitorNative()) {
        setStatus(null);
        return null;
      }

      let next = await getPushPermissionStatus();

      if (options?.attemptRegister) {
        if (next === 'granted') {
          await requestPushPermissionAndRegister();
        } else if (next === 'denied') {
          const result = await requestPushPermissionAndRegister({
            afterExternalSettings: true,
          });
          next = result.granted ? 'granted' : await getPushPermissionStatus();
        }
      }

      setStatus(next);
      return next;
    },
    [],
  );

  useEffect(() => {
    queueMicrotask(() => {
      void refresh();
    });
  }, [refresh, active]);

  useEffect(() => {
    if (!active || !isCapacitorNative()) return;

    let listener: Awaited<ReturnType<typeof App.addListener>> | null = null;

    const setup = async () => {
      listener = await App.addListener('appStateChange', ({ isActive }) => {
        if (isActive) {
          void refresh({ attemptRegister: true });
        }
      });
    };

    void setup();

    return () => {
      void listener?.remove();
    };
  }, [active, refresh]);

  return { status, refresh };
}
